import type { AnalyseSocketResponse } from 'src/redux/types/xray';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { InputFilters } from 'src/pages/jobDetails/types';
import type { ApplicantListSub, JobNotesResponse, OrgJobID } from 'src/openapi';
import { LS_KEY_JOB_APPLICANT_REVIEW_FILTERS } from 'src/constants/localStorageKeys';
import { APPLICATION_STATUS_OPTIONS, AIAnalysisStatus } from 'src/constants';
import {
  fetchJobApplicants,
  getApplicantDetails,
  getJobByIdForEmployer,
  fetchAbekusProfile,
  getLabel,
  getAnalysedResume,
  fetchApplicantResume,
  getJobPlanDetails,
  getJobApplicantsMetaData,
  DownloadResume,
  sendBulkMessage,
  fetchApplicantAiAccess,
  unlockApplicantAiScores,
} from '../actions/applicantReview';
import type {
  UpdateApplicantStatusType,
  ApplicantReviewState,
  StoredFilter,
  ApplicantAiAccessDiscountFields,
} from '../types/applicantReview';
import { fetchMonthlyScore } from '../actions/candidateProfile';
import type { SkillsMonthlyProgressFilters } from '../types/candidateProfile';

export const currentFilterInitialState: InputFilters = {
  applicant_status: APPLICATION_STATUS_OPTIONS.filter((option) =>
    ['pending', 'selected', 'shortlisted', 'hold'].includes(option.value),
  ),
  sort_order: 'desc',
  user_search_query: '',
  sort_by: 'date',
  assessment_score: { max: null, min: null },
  match_percentile: { max: null, min: null },
  profile_rating: { max: null, min: null },
  experience: { max: null, min: null },
  internship_experience: { max: null, min: null },
  cgpa: { max: null, min: null },
  passing_year: { max: null, min: null },
  notice_period: { label: 'All', value: 'all' },
  applicant_tag: [],
  resume_uploaded: { label: 'Show All', value: 'all' },
  is_applicant_seen: { label: 'Show All', value: 'all' },
  matching_locations: null,
  matching_skills: null,
  missing_skills: null,
  colleges: null,
  job_roles: null,
  skill_score: null,
  assessment_screening: null,
};

export const ERROR_RESUME_DOWNLOAD_LIMIT_REACHED =
  'max_resume_download_limit_exhausted';
export const ERROR_VIEW_APPLICANT_LIMIT_REACHED =
  'subscribe_to_premium_to_view_more_applicants';

const initialState: ApplicantReviewState = {
  currentApplicantIndex: -1,
  currentApplicantDetails: undefined,
  fetchingAbekusProfile: false,
  fetchingLevel: false,
  clickedApplicantId: undefined,
  requestedApplicantNotFound: false,
  currentFilters: null,
  jobData: undefined,
  selectedApplicant: [],
  selectAll: false,
  level: {},
  showBulkActionState: false,
  isLoading: false,
  isResumeFetching: false,
  resumeDataURL: undefined,
  isViewLimitReached: false,
  isUnlockDialogOpen: false,
  jobPlanDetails: null,
  orgJobID: null,
  listVersion: 0,
  applicantData: undefined,
  currentApplicantAnalysisData: undefined,
  list: undefined,
  updateStatus: undefined,
  userAbekusProfile: undefined,
  currentApplicantMonthlyProgressData: null,
  skillsMonthlyProgressFilters: {
    endMonth: new Date().toISOString(),
    selectedSkill: null,
  },
  jobApplicantsMetaData: null,
  bulkAnalysis: {
    batchId: null,
    total: 0,
    done: 0,
    running: false,
    pendingReorder: false,
  },
  applicantAiAccess: {
    isUnlocked: false,
    applicantCount: 0,
    cost: 250,
    originalCost: 250,
    isFirstTimeDiscount: false,
    loading: false,
  },
  placementRefreshKey: 0,
};

const AI_FIT_SORT_BY = 'resume_analysis_overall_score';

// Order the list scored-first (highest AI fit desc); unscored keep their
// relative order after (Array.prototype.sort is stable). Used to float scored
// candidates to the top WHILE a ranking is being processed, so ready results
// surface without staying buried in the fetched (date) order. Invoked from the
// throttled `reorderByAiFit` flush (not per score event) so the list settles on
// a calm cadence instead of reshuffling under the reader on every socket tick.
/* eslint-disable no-param-reassign */
const applyAiFitSort = (state: ApplicantReviewState) => {
  const { list } = state;
  if (!list?.all_applicants || list.all_applicants.length === 0) {
    return;
  }
  list.all_applicants = [...list.all_applicants].sort((x, y) => {
    const xs = x.resume_analysis_overall_score;
    const ys = y.resume_analysis_overall_score;
    if (xs != null && ys != null) {
      return ys - xs;
    }
    if (xs != null) {
      return -1;
    }
    if (ys != null) {
      return 1;
    }
    return 0;
  });
  // Keep the selected candidate + prev/next counter pointing at the same person
  // after the reshuffle (selection is by id, the counter is by index).
  if (state.clickedApplicantId) {
    const idx = list.all_applicants.findIndex(
      (a) => a.id === state.clickedApplicantId,
    );
    if (idx !== -1) {
      state.currentApplicantIndex = idx;
    }
  }
};

const jobApplicantSlice = createSlice({
  name: 'jobApplicant',
  initialState,
  reducers: {
    setHiringStatus: (state, action) => {
      state?.list?.all_applicants?.forEach((record) => {
        if (record.id === action.payload.id) {
          record.hiring_status = action.payload.hiring_status;
        }
      });
    },
    setShowBulkActionsState: (state, action) => {
      state.showBulkActionState = action.payload;
    },
    setUnlockDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isUnlockDialogOpen = action.payload;
    },
    bumpPlacementRefreshKey: (state) => {
      state.placementRefreshKey += 1;
    },
    setCurrentFilters: (state, action: PayloadAction<InputFilters>) => {
      const storedApplicantReviewFilters = localStorage.getItem(
        LS_KEY_JOB_APPLICANT_REVIEW_FILTERS,
      );
      const jobId = state.orgJobID?.job_id;
      if (storedApplicantReviewFilters) {
        let jobsFilter: StoredFilter[] = JSON.parse(
          storedApplicantReviewFilters,
        );
        if (jobsFilter.findIndex((val) => val.jobId === jobId) !== -1) {
          jobsFilter = jobsFilter.map((val) => {
            const newVal = { ...val };
            if (newVal.jobId === jobId) {
              newVal.filter = action.payload;
            }
            return newVal;
          });
        } else if (jobsFilter.length > 10) {
          jobsFilter = jobsFilter.slice(1);
          jobsFilter.push({
            jobId: jobId || '',
            filter: action.payload,
          });
        } else {
          jobsFilter.push({
            jobId: jobId || '',
            filter: action.payload,
          });
        }
        localStorage.setItem(
          LS_KEY_JOB_APPLICANT_REVIEW_FILTERS,
          JSON.stringify(jobsFilter),
        );
      } else {
        localStorage.setItem(
          LS_KEY_JOB_APPLICANT_REVIEW_FILTERS,
          JSON.stringify([
            {
              jobId: jobId || '',
              filter: action.payload,
            },
          ]),
        );
      }

      state.currentFilters = action.payload;
      state.skillsMonthlyProgressFilters =
        initialState.skillsMonthlyProgressFilters;
      state.resumeDataURL = undefined;
      state.isViewLimitReached = false;
    },
    setOrgJobId: (state, action: PayloadAction<OrgJobID>) => {
      const jobId = action.payload.job_id;
      const storedApplicantReviewFilters = localStorage.getItem(
        LS_KEY_JOB_APPLICANT_REVIEW_FILTERS,
      );

      if (storedApplicantReviewFilters && jobId) {
        const jobsFilter: StoredFilter[] = JSON.parse(
          storedApplicantReviewFilters || '[]',
        );
        const applicantReviewFilterObj = jobsFilter?.find(
          (val) => val.jobId === jobId,
        );
        if (
          applicantReviewFilterObj &&
          // This is temporary check. Need to remove later.
          applicantReviewFilterObj.filter?.resume_uploaded?.label
        ) {
          state.currentFilters = {
            ...currentFilterInitialState,
            ...state.currentFilters,
            ...applicantReviewFilterObj?.filter,
          };
        } else {
          state.currentFilters = { ...currentFilterInitialState };
        }
      } else {
        state.currentFilters = { ...currentFilterInitialState };
      }
      state.orgJobID = action.payload;
      state.isViewLimitReached = false;
    },
    resetJobData: () => initialState,
    setCurrentApplicantData: (
      state,
      action: PayloadAction<{
        currentApplicantIndex: number;
        currentApplicantDetails: ApplicantListSub;
      }>,
    ) => {
      state.currentApplicantIndex = action.payload.currentApplicantIndex;
      state.currentApplicantDetails = action.payload.currentApplicantDetails;
      state.clickedApplicantId = action.payload.currentApplicantDetails.id;

      // reset other data
      state.applicantData = undefined;
      state.currentApplicantAnalysisData = undefined;
      state.resumeDataURL = undefined;
      state.level = undefined;
      state.userAbekusProfile = undefined;
      state.skillsMonthlyProgressFilters =
        initialState.skillsMonthlyProgressFilters;
      state.isViewLimitReached = false;
    },
    resetCurrentApplicantData: (state) => {
      state.currentApplicantIndex = -1;
      state.clickedApplicantId = undefined;
      state.currentApplicantDetails = undefined;
      state.applicantData = undefined;
      state.resumeDataURL = undefined;
      state.currentApplicantAnalysisData = undefined;
      state.currentApplicantMonthlyProgressData = null;
      state.isViewLimitReached = false;
    },
    updateCurrentHiringStatus: (
      state,
      action: PayloadAction<UpdateApplicantStatusType>,
    ) => {
      if (state.currentApplicantDetails?.id === action.payload.applicantId) {
        if (action.payload.hiringStatus) {
          state.currentApplicantDetails.hiring_status =
            action.payload.hiringStatus;
        }

        if (action.payload.ratingStatus !== undefined) {
          state.currentApplicantDetails.rating = action.payload.ratingStatus;
        }
      }
      if (state?.list?.all_applicants) {
        state.list.all_applicants = state.list.all_applicants.map(
          (applicant) => {
            if (applicant.id === action.payload.applicantId) {
              const updatedApplicant = { ...applicant };
              if (action.payload?.hiringStatus) {
                updatedApplicant.hiring_status = action.payload.hiringStatus;
              }
              if (action.payload?.ratingStatus) {
                updatedApplicant.rating = action.payload.ratingStatus;
              }
              return updatedApplicant;
            }
            return applicant;
          },
        );
      }
      state.updateStatus = action.payload;
    },
    updateBulkStatus: (
      state,
      action: PayloadAction<{
        isSelectedAll?: boolean;
        applicantIds?: string[];
        status: string;
      }>,
    ) => {
      if (!state.list?.all_applicants) {
        return;
      }
      if (action.payload.isSelectedAll) {
        state.list.all_applicants = state.list.all_applicants.map((val) => ({
          ...val,
          hiring_status: action.payload.status,
        }));
      } else {
        state.list.all_applicants = state.list.all_applicants.map((val) => {
          if (action.payload.applicantIds?.includes(val.id ?? '')) {
            return {
              ...val,
              hiring_status: action.payload.status,
            };
          }
          return val;
        });
      }
    },
    resetFilter: (state) => {
      state.currentFilters = { ...currentFilterInitialState };
      const storedApplicantReviewFilters = localStorage.getItem(
        LS_KEY_JOB_APPLICANT_REVIEW_FILTERS,
      );
      if (storedApplicantReviewFilters) {
        let jobsFilter: StoredFilter[] = JSON.parse(
          storedApplicantReviewFilters,
        );
        jobsFilter = jobsFilter.filter(
          (val) => val.jobId !== state.orgJobID?.org_id,
        );
        localStorage.setItem(
          LS_KEY_JOB_APPLICANT_REVIEW_FILTERS,
          JSON.stringify(jobsFilter),
        );
      }
      state.resumeDataURL = undefined;
    },
    selectApplicant: (state, action: PayloadAction<ApplicantListSub>) => {
      state.selectedApplicant.push(action.payload);
      if (state.selectedApplicant.length === 1) {
        state.showBulkActionState = true;
      }
    },
    deselectApplicant: (state, action: PayloadAction<ApplicantListSub>) => {
      state.selectedApplicant = state.selectedApplicant.filter(
        (applicant) => applicant.id !== action.payload.id,
      );

      if (state.selectedApplicant.length === 0) {
        state.showBulkActionState = false;
      }
    },
    toggleSelectAll: (state) => {
      state.selectAll = !state.selectAll;
    },
    deselectAll: (state) => {
      state.selectedApplicant = [];
      state.selectAll = false;
    },
    onApplicantAnalysisComplete: (
      state,
      action: PayloadAction<AnalyseSocketResponse>,
    ) => {
      if (state.list?.all_applicants) {
        state.list.all_applicants = state.list.all_applicants.map((val) => {
          if (val.id === action.payload.analysis_id) {
            val.resume_analysis_overall_score = action.payload.overall_score;
            val.resume_analysis_status = action.payload.status;
          }
          return val;
        });

        if (
          state.applicantData?.id === action.payload.analysis_id &&
          state.applicantData
        ) {
          state.applicantData.resume_analysis_status = action.payload.status;
        }

        if (
          state.currentApplicantDetails &&
          state.currentApplicantDetails?.id === action.payload.analysis_id
        ) {
          state.currentApplicantDetails.resume_analysis_status =
            action.payload.status;
        }

        // The score lights up in place immediately (the ring above). Defer the
        // actual reorder: flag it so the live-ranking controller folds this and
        // any sibling score events into one throttled, scroll-aware re-sort,
        // instead of yanking the list on every socket tick.
        if (state.bulkAnalysis.running) {
          state.bulkAnalysis.pendingReorder = true;
        }
      }
    },
    // Optimistic flip when the employer retries a failed/stale analysis: back to
    // Processing with a fresh modified_on so the staleness check doesn't
    // immediately re-flag it. Polling then picks up the real terminal status.
    markApplicantReanalyzing: (
      state,
      action: PayloadAction<{ applicantId: string; at: string }>,
    ) => {
      const { applicantId, at } = action.payload;
      if (state.list?.all_applicants) {
        state.list.all_applicants = state.list.all_applicants.map((val) => {
          if (val.id === applicantId) {
            val.resume_analysis_status = 'Processing';
            val.modified_on = at;
          }
          return val;
        });
      }
      if (state.currentApplicantDetails?.id === applicantId) {
        state.currentApplicantDetails.resume_analysis_status = 'Processing';
        state.currentApplicantDetails.modified_on = at;
      }
      if (state.applicantData?.id === applicantId) {
        state.applicantData.resume_analysis_status = 'Processing';
      }
    },
    updateCurrentResumeApplicantNotes: (
      state,
      action: PayloadAction<JobNotesResponse[]>,
    ) => {
      state.applicantData = {
        ...state.applicantData,
        user_notes: action.payload,
      };
    },
    updateCurrentResumeApplicantTags: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.applicantData = {
        ...state.applicantData,
        applicant_tag: action.payload,
      };
      if (state.list?.all_applicants) {
        state.list.all_applicants = state.list.all_applicants.map((val) => {
          if (val.id === state.applicantData?.id) {
            val.applicant_tag = action.payload;
          }
          return val;
        });
      }
    },
    clearCurrentApplicantDmUnread: (state) => {
      state.currentApplicantDetails = {
        ...state.currentApplicantDetails,
        unread_message_count: 0,
      };
      if (state.list?.all_applicants) {
        state.list.all_applicants = state.list.all_applicants.map((val) => {
          if (val.id === state.currentApplicantDetails?.id) {
            val.unread_message_count = 0;
          }
          return val;
        });
      }
    },
    setCurrentApplicantChatStarted: (state, action: PayloadAction<boolean>) => {
      if (
        action.payload === true &&
        state.currentApplicantDetails?.is_chat_started === false &&
        state.jobData?.job
      ) {
        state.jobData.job.total_chats_initiated =
          (state.jobData.job.total_chats_initiated ?? 0) + 1;
      }

      state.currentApplicantDetails = {
        ...state.currentApplicantDetails,
        is_chat_started: action.payload,
      };
      if (state.list?.all_applicants) {
        state.list.all_applicants = state.list.all_applicants.map((val) => {
          if (val.id === state.currentApplicantDetails?.id) {
            val.is_chat_started = action.payload;
          }
          return val;
        });
      }
    },
    setSkillsMonthlyProgressFilters: (
      state,
      action: PayloadAction<SkillsMonthlyProgressFilters>,
    ) => {
      state.skillsMonthlyProgressFilters = action.payload;
    },
    startBulkAnalysis: (
      state,
      action: PayloadAction<{ batchId: string | null; total: number }>,
    ) => {
      state.bulkAnalysis = {
        batchId: action.payload.batchId,
        total: action.payload.total,
        done: 0,
        running: action.payload.total > 0,
        pendingReorder: false,
      };
    },
    setBulkProgress: (
      state,
      action: PayloadAction<{
        batch_id: string;
        done: number;
        total: number;
      }>,
    ) => {
      // Ignore progress events from a different (e.g. stale) batch.
      if (
        state.bulkAnalysis.batchId &&
        state.bulkAnalysis.batchId !== action.payload.batch_id
      ) {
        return;
      }
      state.bulkAnalysis.batchId = action.payload.batch_id;
      state.bulkAnalysis.done = action.payload.done;
      state.bulkAnalysis.total = action.payload.total;
      state.bulkAnalysis.running = action.payload.done < action.payload.total;
      // Returning to a job whose ranking is still running: flag a reorder so the
      // controller floats the already-scored candidates up on its next tick,
      // rather than leaving them buried in the fetched (date) order.
      if (state.bulkAnalysis.running) {
        state.bulkAnalysis.pendingReorder = true;
      }
    },
    // Flush any deferred score events into a single re-sort. Driven by the
    // live-ranking controller on a throttled, scroll-aware cadence. `force`
    // (the final settle once scoring finishes) re-sorts even with nothing
    // pending. Respects the employer's sort choice: only reorders while the
    // list is actually sorted by AI fit, so a manual switch to date/experience
    // isn't overridden.
    reorderByAiFit: (state, action: PayloadAction<boolean | undefined>) => {
      const force = action.payload === true;
      if (!force && !state.bulkAnalysis.pendingReorder) {
        return;
      }
      state.bulkAnalysis.pendingReorder = false;
      if (state.currentFilters?.sort_by === AI_FIT_SORT_BY) {
        applyAiFitSort(state);
      }
    },
    resetBulkProgress: (state) => {
      state.bulkAnalysis = { ...initialState.bulkAnalysis };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchJobApplicants.fulfilled, (state, action) => {
      if (action.payload.skipStore) {
        return;
      }
      if (!action.meta.arg.payload.count_only) {
        state.list = action.payload.data;
        state.listVersion += 1;
      }
      if (
        !action.meta.arg.payload.count_only &&
        (action.payload.selectedApplicantUsername ||
          action.payload.selectFirstApplicant)
      ) {
        const applicants = action.payload.data.all_applicants ?? [];
        const requestedUsername = action.payload.selectedApplicantUsername;
        const idx = requestedUsername
          ? applicants.findIndex(
              (val) => val.user?.username === requestedUsername,
            )
          : -1;

        // A deep link that names a candidate must never fall through to index
        // 0: the filtered list rarely contains everyone (rejected applicants
        // are excluded by default), and picking the first row instead renders
        // one candidate's application under another candidate's name.
        if (requestedUsername ? idx === -1 : applicants.length === 0) {
          state.requestedApplicantNotFound = Boolean(requestedUsername);
          state.currentApplicantIndex = -1;
          state.currentApplicantDetails = undefined;
          state.clickedApplicantId = undefined;
          // The right pane renders off applicantData, so leaving it behind kept
          // the previous candidate's profile — name, phone, email — on screen
          // next to a list that no longer contains them.
          state.applicantData = undefined;
          state.currentApplicantAnalysisData = undefined;
          state.resumeDataURL = undefined;
          state.currentApplicantMonthlyProgressData = null;
        } else {
          const applicantIdx = idx === -1 ? 0 : idx;
          const applicant = applicants[applicantIdx];
          state.requestedApplicantNotFound = false;
          state.currentApplicantIndex = applicantIdx;
          state.currentApplicantDetails = applicant;
          state.clickedApplicantId = applicant?.id || '';
        }
      }
      state.isLoading = false;
    });
    builder.addCase(fetchJobApplicants.pending, (state, action) => {
      if (!action.meta.arg.payload.count_only) {
        state.isLoading = true;
      }
    });
    builder.addCase(fetchJobApplicants.rejected, (state, action) => {
      if (!action.meta.arg.payload.count_only) {
        state.isLoading = false;
      }
    });
    builder.addCase(getApplicantDetails.pending, (state) => {
      state.isViewLimitReached = false;
    });
    builder.addCase(getApplicantDetails.fulfilled, (state, action) => {
      state.applicantData = action.payload;
      // The unlock flow refetches details after unlocking; keep the list card's
      // lock icon in sync so it flips to unlocked without a page refresh.
      if (state.list?.all_applicants && action.payload.is_unlocked != null) {
        state.list.all_applicants = state.list.all_applicants.map((val) =>
          val.id === action.payload.id
            ? { ...val, is_unlocked: action.payload.is_unlocked }
            : val,
        );
      }
      // The analysis status flips server-side after an unlock; keep the list
      // item (and list card) in sync so the existing analysis fetch fires.
      const analysisStatus = action.payload.resume_analysis_status;
      if (
        state.currentApplicantDetails &&
        state.currentApplicantDetails.id === action.payload.id &&
        analysisStatus
      ) {
        state.currentApplicantDetails.resume_analysis_status = analysisStatus;
        if (state.list?.all_applicants) {
          state.list.all_applicants = state.list.all_applicants.map((val) =>
            val.id === action.payload.id
              ? { ...val, resume_analysis_status: analysisStatus }
              : val,
          );
        }
      }
      if (
        state.list?.all_applicants &&
        !state.currentApplicantDetails?.is_application_seen &&
        state.currentApplicantDetails?.id === action.payload.id
      ) {
        state.list.all_applicants = state.list.all_applicants.map((val) => {
          if (val.id === action.payload.id) {
            return { ...val, is_application_seen: true };
          }
          return { ...val };
        });
      }
    });
    builder.addCase(getApplicantDetails.rejected, (state, action) => {
      if (
        (action?.payload as any)?.error === ERROR_VIEW_APPLICANT_LIMIT_REACHED
      ) {
        state.isViewLimitReached = true;
      }
    });
    builder.addCase(getAnalysedResume.fulfilled, (state, action) => {
      if (state.clickedApplicantId === action.payload.id) {
        state.currentApplicantAnalysisData = action.payload;
      }
      // Sync the canonical AI fit score onto the list card + selected detail so
      // the sidebar ScoreRing updates in place instead of showing 0 until a
      // refresh. Use overall_score (the single source of truth the backend keeps
      // stable) — NOT response.overall.score, whose value can lag the column on
      // legacy rows and made the list badge visibly jump on selection.
      const score = action.payload.overall_score;
      if (typeof score === 'number') {
        if (state.list?.all_applicants) {
          state.list.all_applicants = state.list.all_applicants.map((val) =>
            val.id === action.payload.id
              ? {
                  ...val,
                  resume_analysis_overall_score: score,
                  resume_analysis_status: AIAnalysisStatus.Success,
                }
              : val,
          );
        }
        const detail = state.currentApplicantDetails;
        if (detail && detail.id === action.payload.id) {
          detail.resume_analysis_overall_score = score;
          detail.resume_analysis_status = AIAnalysisStatus.Success;
        }
      }
    });
    builder.addCase(getJobByIdForEmployer.fulfilled, (state, action) => {
      state.jobData = action.payload;
    });
    builder.addCase(fetchAbekusProfile.pending, (state) => {
      state.fetchingAbekusProfile = true;
    });
    builder.addCase(fetchAbekusProfile.fulfilled, (state, action) => {
      state.fetchingAbekusProfile = false;
      state.userAbekusProfile = action.payload;
    });
    builder.addCase(fetchAbekusProfile.rejected, (state) => {
      state.fetchingAbekusProfile = false;
    });

    builder.addCase(fetchMonthlyScore.fulfilled, (state, action) => {
      if (action.meta.arg.type === 'review') {
        state.currentApplicantMonthlyProgressData = action.payload;
      }
    });
    builder.addCase(getLabel.pending, (state) => {
      state.fetchingLevel = true;
    });
    builder.addCase(getLabel.fulfilled, (state, action) => {
      state.level = action.payload;
      state.fetchingLevel = false;
    });
    builder.addCase(getLabel.rejected, (state) => {
      state.fetchingLevel = false;
    });
    builder.addCase(fetchApplicantResume.pending, (state) => {
      state.isResumeFetching = true;
      state.resumeDataURL = undefined;
      state.resumeMimeType = undefined;
    });
    builder.addCase(fetchApplicantResume.fulfilled, (state, action) => {
      state.resumeDataURL = action.payload.data_url;
      state.resumeMimeType = action.payload.content_type;
      state.isResumeFetching = false;
    });
    builder.addCase(fetchApplicantResume.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isResumeFetching = false;
      }
      if (
        (action.payload as any)?.error === ERROR_RESUME_DOWNLOAD_LIMIT_REACHED
      ) {
        state.isViewLimitReached = true;
      }
    });
    builder.addCase(getJobPlanDetails.fulfilled, (state, action) => {
      state.jobPlanDetails = action.payload;
    });
    builder.addCase(getJobApplicantsMetaData.fulfilled, (state, action) => {
      state.jobApplicantsMetaData = action.payload;
    });
    builder.addCase(DownloadResume.fulfilled, (state, action) => {
      if (
        state?.jobData?.job &&
        state.applicantData &&
        action.meta.arg.applicantId === state.applicantData.id &&
        !state.applicantData.is_resume_downloaded
      ) {
        state.applicantData.is_resume_downloaded = true;
        state.jobData.job.total_resume_downloaded =
          (state.jobData.job.total_resume_downloaded ?? 0) + 1;
      }
    });
    builder.addCase(sendBulkMessage.fulfilled, (state, action) => {
      const userIds = action.meta.arg?.payload?.custom_applicant_user_ids;
      if (!state.list?.all_applicants || !state.jobData?.job) return;

      let newChatsCount = 0;

      state.list.all_applicants = state.list.all_applicants.map((applicant) => {
        const isTargetUser = userIds?.includes(applicant.user?.id!);

        if (isTargetUser && applicant.is_chat_started === false) {
          newChatsCount += 1;

          if (state.currentApplicantDetails?.user?.id === applicant.user?.id) {
            state.currentApplicantDetails = {
              ...state.currentApplicantDetails,
              is_chat_started: true,
            };
          }

          return {
            ...applicant,
            is_chat_started: true,
          };
        }

        return applicant;
      });

      if (newChatsCount > 0) {
        state.jobData.job.total_chats_initiated =
          (state.jobData.job.total_chats_initiated ?? 0) + newChatsCount;
      }
    });
    builder.addCase(fetchApplicantAiAccess.pending, (state) => {
      state.applicantAiAccess.loading = true;
    });
    builder.addCase(fetchApplicantAiAccess.fulfilled, (state, action) => {
      // Discount fields aren't in the generated client type yet — read them off
      // the payload until the spec is regenerated (see ApplicantAiAccessDiscountFields).
      const payload = action.payload as typeof action.payload &
        ApplicantAiAccessDiscountFields;
      state.applicantAiAccess.loading = false;
      state.applicantAiAccess.isUnlocked = payload.is_unlocked ?? false;
      state.applicantAiAccess.applicantCount = payload.applicant_count ?? 0;
      const cost = payload.cost ?? state.applicantAiAccess.cost;
      const originalCost = payload.original_cost ?? cost;
      state.applicantAiAccess.cost = cost;
      state.applicantAiAccess.originalCost = originalCost;
      state.applicantAiAccess.isFirstTimeDiscount =
        payload.is_first_time_discount ?? originalCost > cost;
    });
    builder.addCase(fetchApplicantAiAccess.rejected, (state) => {
      state.applicantAiAccess.loading = false;
    });
    builder.addCase(unlockApplicantAiScores.fulfilled, (state, action) => {
      if (action.payload.is_unlocked) {
        state.applicantAiAccess.isUnlocked = true;
      }
    });
  },
});
/* eslint-enable no-param-reassign */

const { actions, reducer } = jobApplicantSlice;
export const {
  setHiringStatus,
  setShowBulkActionsState,
  setUnlockDialogOpen,
  bumpPlacementRefreshKey,
  markApplicantReanalyzing,
  setCurrentFilters,
  updateCurrentHiringStatus,
  setOrgJobId,
  resetJobData,
  resetFilter,
  selectApplicant,
  deselectApplicant,
  toggleSelectAll,
  deselectAll,
  updateBulkStatus,
  setCurrentApplicantData,
  onApplicantAnalysisComplete,
  resetCurrentApplicantData,
  updateCurrentResumeApplicantNotes,
  updateCurrentResumeApplicantTags,
  setCurrentApplicantChatStarted,
  clearCurrentApplicantDmUnread,
  setSkillsMonthlyProgressFilters,
  startBulkAnalysis,
  setBulkProgress,
  reorderByAiFit,
  resetBulkProgress,
} = actions;
export default reducer;
